import { createListController } from '@app/components/list';
import { toEntityActionListState } from '@app/features/next-soup/actions';
import { openEntityInSplitFromUnifiedList } from '@app/features/next-soup/utils';
import { SoupEntityContextMenu } from '@app/features/soup';
import { DEBUG_SETTING_KEYS, useDebugSetting } from '@app/lib/debugSettings';
import { useGlobalNotificationSource } from '@components/app/GlobalAppState';
import { type PillTabItem, PillTabs } from '@components/app/mobile/PillTabs';
import { PullToRefresh } from '@components/app/mobile/PullToRefresh';
import { SplitHeaderLeft } from '@components/app/split-layout/components/SplitHeader';
import { useSplitPanelOrThrow } from '@components/app/split-layout/layoutUtils';
import { toast } from '@core/component/Toast/Toast';
import { useUserId } from '@core/context/user';
import type { ChannelEntity } from '@entity';
import { isMutedItem } from '@entity/utils/notification';
import SpinnerIcon from '@phosphor/spinner.svg';
import { hydrateChannelNotificationSelection } from '@queries/channel/notification-selection';
import { createElementSize } from '@solid-primitives/resize-observer';
import { Button } from '@ui';
import {
  createMemo,
  createSignal,
  createUniqueId,
  Match,
  onCleanup,
  Show,
  Switch,
} from 'solid-js';
import { Virtualizer, type VirtualizerHandle } from 'virtua/solid';
import { useChannelsView } from '../channels-view-context';
import type { ChannelsDataSource } from '../queries';
import type { ChannelsQueryScope } from '../types';
import { channelMentionsUser } from '../utils';
import { ChannelsEmptyState } from './ChannelsEmptyState';
import {
  CHANNEL_ACTION_VIEW_CONTEXT,
  CONVERSATION_CARD_HEIGHT,
  ConversationCard,
} from './rail/ChannelRailItems';
import { useChannelCalls } from './rail/hooks/useChannelCalls';
import { useChannelRailActivity } from './rail/hooks/useChannelRailActivity';

const MOBILE_CHANNEL_TABS: PillTabItem<ChannelsQueryScope>[] = [
  { value: 'recents', label: 'Recent' },
  { value: 'channels', label: 'Channels' },
  { value: 'direct_messages', label: 'DMs' },
];
const MOBILE_TAB_STRIP_CLASS =
  '-ml-(--mobile-chrome-gutter) w-[100cqw] max-w-none flex-none';
const MOBILE_TAB_CONTENT_CLASS = 'px-(--mobile-chrome-gutter)';
const MOBILE_CHANNEL_BUFFER_SIZE = CONVERSATION_CARD_HEIGHT * 6;
const LOAD_MORE_THRESHOLD = 300;

export function ChannelsMobileView(props: {
  source: ChannelsDataSource;
  tab: ChannelsQueryScope;
  onTabChange: (tab: ChannelsQueryScope) => void;
}) {
  const panel = useSplitPanelOrThrow();
  const notificationSource = useGlobalNotificationSource();
  const currentUserId = useUserId();
  const { state, setSelectedChannelId } = useChannelsView();
  const [viewport, setViewport] = createSignal<HTMLDivElement>();
  const [virtualizer, setVirtualizer] = createSignal<VirtualizerHandle>();
  const [topSpacer, setTopSpacer] = createSignal<HTMLDivElement>();
  const topSpacerSize = createElementSize(topSpacer);
  const forceEmptyState = useDebugSetting(
    DEBUG_SETTING_KEYS.FORCE_EMPTY_STATES
  );
  const listId = createUniqueId();
  const channelCalls = useChannelCalls();
  const visibleChannels = createMemo(() => props.source.items());
  const channelActivity = useChannelRailActivity(visibleChannels, channelCalls);
  const actionController = createListController({
    items: visibleChannels,
    getKey: (channel) => channel.id,
    isSelectable: () => false,
  });
  const actionList = toEntityActionListState({
    controller: actionController,
    getEntity: (channel) => channel,
  });

  const selectTab = (tab: ChannelsQueryScope) => {
    props.onTabChange(tab);
    viewport()?.scrollTo({ top: 0 });
  };

  const topInset = () => topSpacerSize.height ?? 0;

  function loadNextPage() {
    if (
      props.source.isFetching() ||
      props.source.isLoadingMore() ||
      !props.source.hasMore()
    ) {
      return;
    }

    void props.source.loadMore();
  }

  function checkNearEnd(offset?: number) {
    const handle = virtualizer();
    if (!handle) return;

    const distance =
      handle.scrollSize - handle.viewportSize - (offset ?? handle.scrollOffset);
    if (distance >= LOAD_MORE_THRESHOLD) return;

    loadNextPage();
  }

  let opening = 0;
  onCleanup(() => {
    opening += 1;
  });
  const openChannel = async (channel: ChannelEntity) => {
    const request = ++opening;
    setSelectedChannelId(channel.id);
    try {
      const full = await hydrateChannelNotificationSelection(
        channel,
        notificationSource.withLocalOverrides
      );
      if (request !== opening) return;
      await openEntityInSplitFromUnifiedList(full, {
        splitHandle: panel.handle,
        referredFrom: 'channels',
        notificationSource,
      });
    } catch (error) {
      if (request !== opening) return;
      console.error('Failed to open conversation', error);
      toast.failure('Unable to open conversation. Please try again.');
    }
  };

  return (
    <>
      <SplitHeaderLeft>
        <div class="flex h-full w-full min-w-0 flex-1 items-center">
          <PillTabs
            scrollable
            class={MOBILE_TAB_STRIP_CLASS}
            contentClass={MOBILE_TAB_CONTENT_CLASS}
            items={MOBILE_CHANNEL_TABS}
            value={props.tab}
            onChange={selectTab}
          />
        </div>
      </SplitHeaderLeft>

      <div
        role="tree"
        aria-label={`${MOBILE_CHANNEL_TABS.find((tab) => tab.value === props.tab)?.label ?? 'Channels'} conversations`}
        aria-busy={props.source.isLoadingMore()}
        class="relative size-full min-h-0 overflow-hidden"
      >
        <PullToRefresh
          scrollContainer={viewport}
          onRefresh={props.source.refresh}
        />
        <div
          ref={setViewport}
          class="scrollbar-hidden size-full min-h-0 overflow-y-auto overscroll-none"
        >
          <div
            ref={setTopSpacer}
            aria-hidden="true"
            class="h-[calc(var(--mobile-content-inset-top,0px)+0.75rem)]"
          />
          <Switch>
            <Match when={!forceEmptyState() && props.source.isLoading()}>
              <div class="grid min-h-32 place-items-center text-ink-muted">
                <SpinnerIcon
                  aria-label="Loading conversations"
                  class="size-5 animate-spin"
                />
              </div>
            </Match>
            <Match
              when={
                !forceEmptyState() &&
                props.source.error() &&
                visibleChannels().length === 0
              }
            >
              <div class="flex min-h-32 flex-col items-center justify-center gap-3 px-(--mobile-chrome-gutter) text-sm text-ink-muted">
                <span>Conversations couldn’t be loaded.</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void props.source.refresh()}
                >
                  Try again
                </Button>
              </div>
            </Match>
            <Match when={forceEmptyState() || visibleChannels().length === 0}>
              <ChannelsEmptyState scope={props.tab} />
            </Match>
            <Match when={true}>
              <Virtualizer
                ref={(handle) => setVirtualizer(handle)}
                data={visibleChannels()}
                scrollRef={viewport()}
                startMargin={topInset()}
                itemSize={CONVERSATION_CARD_HEIGHT}
                bufferSize={MOBILE_CHANNEL_BUFFER_SIZE}
                onScroll={checkNearEnd}
              >
                {(channel) => (
                  <SoupEntityContextMenu
                    entity={channel}
                    list={actionList}
                    selectedEntities={() => []}
                    viewContext={CHANNEL_ACTION_VIEW_CONTEXT}
                    class="block w-full"
                    onOpenChange={(open) => {
                      if (!open) return;
                      actionController.focus.set(channel.id, {
                        reason: 'pointer',
                        force: true,
                      });
                    }}
                  >
                    <ConversationCard
                      id={`${listId}-channel:${channel.id}`}
                      class="border-b border-edge-muted/50 px-(--mobile-chrome-gutter) touch:pl-6"
                      channel={channel}
                      showLatestMessage={props.tab === 'recents'}
                      senderId={channel.latestRootMessage?.senderId}
                      mentionedCurrentUser={channelMentionsUser(
                        channel,
                        currentUserId()
                      )}
                      unread={channelActivity
                        .unreadChannelIds()
                        .has(channel.id)}
                      muted={isMutedItem(notificationSource.mutedEntities(), {
                        item_id: channel.id,
                        item_type: 'channel',
                      })}
                      callStatus={channelActivity
                        .callStatuses()
                        .get(channel.id)}
                      incomingCallId={channelActivity
                        .incomingCallIds()
                        .get(channel.id)}
                      selected={state.selectedChannelId === channel.id}
                      focused={false}
                      onActivate={() => openChannel(channel)}
                    />
                  </SoupEntityContextMenu>
                )}
              </Virtualizer>
              <Show when={props.source.isLoadingMore()}>
                <div class="flex h-12 items-center justify-center text-ink-muted">
                  <SpinnerIcon
                    aria-label="Loading more conversations"
                    class="size-4 animate-spin"
                  />
                </div>
              </Show>
              <Show
                when={
                  props.source.error() &&
                  !props.source.isLoadingMore() &&
                  visibleChannels().length > 0
                }
              >
                <div class="flex items-center justify-center gap-2 py-3 text-xs text-ink-muted">
                  <span>Couldn’t load more conversations.</span>
                  <Button
                    variant="outline"
                    size="xs"
                    onClick={() => void props.source.refresh()}
                  >
                    Try again
                  </Button>
                </div>
              </Show>
            </Match>
          </Switch>
          <div
            aria-hidden="true"
            class="h-[max(1rem,var(--mobile-content-inset-bottom,0px))]"
          />
        </div>
      </div>
    </>
  );
}
